import Fuse from 'fuse.js';
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import styled from 'styled-components';
import { Button, DeleteConfirmation, FiltersBar, Icon, Page, SearchInput, SingleSelectFilter } from '~/components';
import { useApi, useConfirmation, useToast, useWorkspace } from '~/contexts';
import { useDocumentTitle, useSearchParams, useSessionStorage } from '~/hooks';
import { colors } from '~/styles';
import { useAssistant } from '../../ai/assistant/AssistantContext';
import { useAssistantProfiles } from './AssistantProfilesContext';
import AssistantProfileCard from './AssistantProfileCard';
import AssistantProfileForm from './AssistantProfileForm';
import AssistantProfileView from './AssistantProfileView';

const Grid = styled.div`
  margin-top: 0.5rem;
  display: grid;
  grid-row-gap: 0.875rem;
  grid-column-gap: 0.875rem;
  grid-template-columns: repeat(auto-fill, minmax(30rem, 1fr));
`;

const SectionHeader = styled.div`
  display: flex;
  align-items: center;
  padding-top: 1.25rem;
  padding-bottom: 0.5rem;
  cursor: pointer;
`;

const SectionTitle = styled.div`
  font-size: 0.8125rem;
  font-weight: 600;
  color: ${colors.grey55};
  text-transform: uppercase;
  letter-spacing: 0.05rem;
`;

const AccordionButton = styled(Button)`
  font-size: 0.75rem;
  color: ${colors.grey55};
  opacity: 0.5;

  &:hover {
    color: ${colors.grey55};
    opacity: 1;
  }
`;

const NoResults = styled.div`
  color: ${colors.grey40};
  font-size: 0.875rem;
  margin: 1.5rem 0;
`;

export default function AssistantProfilesPage() {
  useDocumentTitle('Assistant Profiles');
  const api = useApi();
  const { workspace } = useWorkspace();
  const toast = useToast();
  const confirmation = useConfirmation();
  const { profiles, fetchData } = useAssistantProfiles();
  const { refreshProfiles } = useAssistant();

  const [dialog, setDialog] = useState(null);
  const [params, setParams] = useState(() => ({ q: '', scopeKey: null }));
  const [searchParamsStatus, setSearchParamsStatus] = useState('pending');
  const searchParams = useSearchParams({
    config: useMemo(
      () => ({
        q: { default: '' },
        scopeKey: { valid: ['system', 'workspace', 'member'] },
      }),
      [],
    ),
    sessionKey: 'assistant_profiles_page__params',
    onChange: useCallback((params) => setParams((state) => ({ ...state, ...params })), []),
  });

  useEffect(() => {
    if (searchParamsStatus === 'ready') return;
    searchParams.get().then((params) => {
      if (params) {
        setParams((state) => ({ ...state, ...params }));
        setSearchParamsStatus('ready');
      }
    });
  }, [searchParams, searchParamsStatus]);

  const sectionStorage = useSessionStorage({ key: 'assistant_profiles_page__sections' });

  const [expanded, setExpanded] = useState(() => {
    const defaults = { system: true, workspace: true, personal: true };
    try {
      if (sectionStorage.hasValue()) {
        const value = JSON.parse(sectionStorage.get());
        if (value) {
          for (const key of Object.keys(defaults)) {
            if (key in value) defaults[key] = !!value[key];
          }
        }
      }
    } finally {
      sectionStorage.set(JSON.stringify(defaults));
    }
    return defaults;
  });

  const allExpanded = Object.keys(expanded).every((key) => expanded[key]);

  const toggleSection = (id) => {
    const value = { ...expanded, [id]: !expanded[id] };
    setExpanded(value);
    sectionStorage.set(JSON.stringify(value));
  };

  const toggleAllSections = () => {
    const value = {
      system: !allExpanded,
      workspace: !allExpanded,
      personal: !allExpanded,
    };
    setExpanded(value);
    sectionStorage.set(JSON.stringify(value));
  };

  const sections = useMemo(() => {
    if (searchParamsStatus !== 'ready') return null;

    let filtered = [...profiles];

    if (params.q) {
      const fuse = new Fuse(filtered, {
        keys: ['name', 'description'],
        threshold: 0.1,
        ignoreLocation: true,
      });
      filtered = fuse.search(params.q).map(({ item }) => item);
    }

    if (params.scopeKey) {
      filtered = filtered.filter((p) => p.scopeKey === params.scopeKey);
    }

    const system = filtered.filter((p) => p.scopeKey === 'system');
    const workspaceProfiles = filtered.filter((p) => p.scopeKey === 'workspace');
    const personal = filtered.filter((p) => p.scopeKey === 'member');

    const result = [];
    if (system.length) result.push({ id: 'system', title: 'Standard', profiles: system });
    if (workspaceProfiles.length) result.push({ id: 'workspace', title: 'Workspace', profiles: workspaceProfiles });
    if (personal.length) result.push({ id: 'personal', title: 'Personal', profiles: personal });
    return result;
  }, [profiles, params.q, params.scopeKey, searchParamsStatus]);

  const handleFilter = ({ target: { name, value } }) => {
    setParams((params) => ({ ...params, [name]: value }));
    searchParams.set({ [name]: value });
  };

  const handleCloseDialog = () => setDialog(null);

  const handleNew = () => {
    setDialog(
      <AssistantProfileForm
        onClose={handleCloseDialog}
        onSaved={async () => {
          handleCloseDialog();
          await fetchData();
          refreshProfiles();
        }}
      />,
    );
  };

  const handleView = (profile) => {
    setDialog(<AssistantProfileView profileId={profile.id} onClose={handleCloseDialog} />);
  };

  const handleEdit = (profile) => {
    setDialog(
      <AssistantProfileForm
        profileId={profile.id}
        onClose={handleCloseDialog}
        onSaved={async () => {
          handleCloseDialog();
          await fetchData();
          refreshProfiles();
        }}
        onDeleted={async () => {
          handleCloseDialog();
          await fetchData();
          refreshProfiles();
        }}
      />,
    );
  };

  const handleDelete = async (profile) => {
    await confirmation.prompt((resolve) => (
      <DeleteConfirmation
        resolve={async (confirm) => {
          if (!confirm) {
            resolve(false);
            return;
          }
          await api.www.workspaces(workspace.id).ai().assistantProfiles(profile.id).remove();
          await fetchData();
          refreshProfiles();
          resolve(true);
          toast.success('Assistant profile deleted.');
        }}>
        Are you sure you want to delete this assistant profile?
      </DeleteConfirmation>
    ));
  };

  return (
    <>
      <Page.Header>
        <Page.Info>
          <Page.Eyebrow>Settings</Page.Eyebrow>
          <Page.Title>
            Assistant Profiles
            <AccordionButton style={{ marginLeft: '0.5rem' }} isAnchor onClick={() => toggleAllSections()}>
              <Icon icon={allExpanded ? 'angle-double-down' : 'angle-double-up'} />
            </AccordionButton>
          </Page.Title>
        </Page.Info>

        <Page.Actions>
          <Button onClick={handleNew}>New Profile</Button>
        </Page.Actions>
      </Page.Header>

      <Page.Filters>
        <FiltersBar>
          <div style={{ gridColumn: '1 / 3' }}>
            <SearchInput name="q" value={params.q} onChange={handleFilter} placeholder="Search" autoFocus />
          </div>

          <SingleSelectFilter
            name="scopeKey"
            placeholder="Profile Type"
            onChange={handleFilter}
            value={params.scopeKey}
            options={[
              { id: 'system', name: 'Standard' },
              { id: 'workspace', name: 'Workspace' },
              { id: 'member', name: 'Personal' },
            ]}
          />
        </FiltersBar>
      </Page.Filters>

      {!sections ? null : sections.length === 0 ? (
        <NoResults>No assistant profiles found.</NoResults>
      ) : (
        sections.map((section) => (
          <React.Fragment key={section.id}>
            <SectionHeader onClick={() => toggleSection(section.id)}>
              <SectionTitle>{section.title}</SectionTitle>
              <Icon
                style={{ marginLeft: '0.25rem', fontSize: '0.75rem', color: colors.grey40 }}
                icon={expanded[section.id] ? 'angle-down' : 'angle-up'}
              />
            </SectionHeader>
            {expanded[section.id] && (
              <Grid>
                {section.profiles.map((profile) => (
                  <AssistantProfileCard
                    key={profile.id}
                    profile={profile}
                    onView={() => handleView(profile)}
                    onEdit={() => handleEdit(profile)}
                    onDelete={() => handleDelete(profile)}
                  />
                ))}
              </Grid>
            )}
          </React.Fragment>
        ))
      )}

      {dialog}
    </>
  );
}
